import { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BrandPanel, SocialButtons } from './Login';
import { useAuth } from '@/store/useAuth';
import { resendVerification } from '@/services/authClient';

export default function Register() {
  const authRegister = useAuth((s) => s.register);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Enter your first name';
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    if (form.password.length < 6) errs.password = 'Use at least 6 characters';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await authRegister({
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
      });
      setVerificationSent(true);
    } catch (err) {
      const data = err?.response?.data || {};
      const apiErr =
        data.detail ||
        data.email?.[0] ||
        data.non_field_errors?.[0] ||
        'Registration failed. Please try again.';
      setErrors({ api: apiErr });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(form.email);
      toast('Verification email resent!');
    } catch {
      toast.error('Could not resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
        <BrandPanel />
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-blue-50">
              <MailCheck size={32} className="text-blue-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Check your inbox</h1>
            <p className="mt-2 text-slate-600">We sent a verification link to <strong>{form.email}</strong>. Click it to activate your account.</p>
            <Button onClick={handleResend} disabled={resending} variant="outline" className="mt-6 w-full rounded-xl">
              {resending ? 'Sending…' : 'Resend verification email'}
            </Button>
            <p className="mt-4 text-sm text-slate-500">Already verified? <Link to="/login" className="font-medium text-blue-600 hover:underline">Sign in</Link></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-slate-900">Create account</h1>
          <p className="mt-1 text-slate-600">Join free to save homes and get instant alerts.</p>
          <div className="mt-6"><SocialButtons /></div>
          {(process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_FACEBOOK_APP_ID || process.env.REACT_APP_APPLE_CLIENT_ID) ? (
            <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />OR<span className="h-px flex-1 bg-slate-200" /></div>
          ) : (
            <div className="mt-6" />
          )}
          {errors.api && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.api}</p>}
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">First name</Label>
                <div className="relative mt-1.5"><User size={16} className="absolute left-3 top-3 text-slate-400" /><Input id="first_name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Jane" className="rounded-xl pl-9" data-testid="register-first-name" /></div>
                {errors.first_name && <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>}
              </div>
              <div>
                <Label htmlFor="last_name">Last name</Label>
                <div className="relative mt-1.5"><Input id="last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Doe" className="rounded-xl" data-testid="register-last-name" /></div>
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5"><Mail size={16} className="absolute left-3 top-3 text-slate-400" /><Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="rounded-xl pl-9" data-testid="register-email" /></div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5"><Lock size={16} className="absolute left-3 top-3 text-slate-400" /><Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Create a password" className="rounded-xl pl-9" data-testid="register-password" /></div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading} data-testid="auth-submit-button">
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">Already have an account? <Link to="/login" className="font-medium text-blue-600 hover:underline">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
